import React from 'react';
import { Link, useLocation } from 'react-router-dom';

interface SidebarProps {
  userProfile: any;
  onLogout: () => void;
  isOpen?: boolean;
  onClose?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ userProfile, onLogout, isOpen = false, onClose }) => {
  const location = useLocation();

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  // Role display names mapping
  const getRoleDisplayName = (role: string) => {
    const roleMap = {
      'user': 'TEAM MEMBER',
      'team_member': 'TEAM MEMBER',
      'admin': 'PARTNER', 
      'partner': 'PARTNER',
      'super_admin': 'SUPER ADMIN'
    };
    return roleMap[role as keyof typeof roleMap] || role?.toUpperCase();
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    onLogout();
    onClose?.();
  };

  const menuItems = [
    {
      path: '/my-expenses',
      icon: '💰',
      label: 'My Expenses',
      description: 'Track and manage expenses'
    },
    {
      path: '/submit-expense',
      icon: '➕',
      label: 'Submit Expense',
      description: 'Add new expense'
    },
    {
      path: '/my-reports',
      icon: '📋',
      label: 'My Reports',
      description: 'Monthly expense reports'
    },
    {
      path: '/settings',
      icon: '⚙️',
      label: 'Settings',
      description: 'Profile and preferences'
    }
  ];

  // Add admin menu item only if user is super_admin
  if (userProfile?.role === 'super_admin') {
    menuItems.push({
      path: '/admin',
      icon: '👑',
      label: 'Admin Panel',
      description: 'User & expense management'
    });
  }

  return (
    <>
      <div className={`sidebar ${isOpen ? 'sidebar--open' : ''}`}>
      {/* Header */}
      <div style={{
        padding: '20px',
        borderBottom: '1px solid #e5e7eb'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '8px'
        }}>
          <div style={{
            width: '32px',
            height: '32px',
            backgroundColor: '#3b82f6',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '16px',
            fontWeight: 'bold'
          }}>
            E
          </div>
          <div>
            <h1 style={{
              margin: 0,
              fontSize: '18px',
              fontWeight: '700',
              color: '#111827'
            }}>
              ExpenseFlow
            </h1>
            <p style={{
              margin: 0,
              fontSize: '12px',
              color: '#6b7280'
            }}>
              Professional Expense Management
            </p>
          </div>
        </div>
      </div>

      {/* Navigation Menu */}
      <nav style={{
        flex: 1,
        padding: '16px 0'
      }}>
        {menuItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px 20px',
              textDecoration: 'none',
              color: isActive(item.path) ? '#3b82f6' : '#374151',
              backgroundColor: isActive(item.path) ? '#eff6ff' : 'transparent',
              borderRight: isActive(item.path) ? '3px solid #3b82f6' : '3px solid transparent',
              transition: 'all 0.2s ease'
            }}
            onClick={() => onClose?.()}
            onMouseEnter={(e) => {
              if (!isActive(item.path)) {
                e.currentTarget.style.backgroundColor = '#f9fafb';
              }
            }}
            onMouseLeave={(e) => {
              if (!isActive(item.path)) {
                e.currentTarget.style.backgroundColor = 'transparent';
              }
            }}
          >
            <span style={{ fontSize: '18px' }}>{item.icon}</span>
            <div style={{ flex: 1 }}>
              <div style={{
                fontSize: '14px',
                fontWeight: isActive(item.path) ? '600' : '500',
                lineHeight: '1.2'
              }}>
                {item.label}
              </div>
              <div style={{
                fontSize: '11px',
                color: '#9ca3af',
                lineHeight: '1.2'
              }}>
                {item.description}
              </div>
            </div>
          </Link>
        ))}
      </nav>

      {/* User Profile Section */}
      <div style={{
        padding: '16px',
        borderTop: '1px solid #e5e7eb',
        marginTop: 'auto'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '12px'
        }}>
          <div style={{
            width: '32px',
            height: '32px',
            backgroundColor: '#10b981',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '14px',
            fontWeight: '600'
          }}>
            {userProfile?.email?.charAt(0).toUpperCase() || 'U'}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>
              {userProfile?.first_name} {userProfile?.last_name}
            </div>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>
              {userProfile?.email}
            </div>
            <div style={{
              fontSize: '11px',
              color: '#10b981',
              backgroundColor: '#dcfce7',
              padding: '2px 6px',
              borderRadius: '4px',
              display: 'inline-block',
              marginTop: '4px'
            }}>
              {getRoleDisplayName(userProfile?.role)}
            </div>
          </div>
        </div>
        <button
          onClick={handleLogout}
          style={{
            width: '100%',
            padding: '12px 16px',
            backgroundColor: 'transparent',
            border: '1px solid #ef4444',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '500',
            color: '#ef4444',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            textAlign: 'left',
            transition: 'all 0.2s',
            marginTop: '8px'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#ef4444';
            e.currentTarget.style.color = 'white';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = '#ef4444';
          }}
        >
          <span style={{ fontSize: '16px' }}>🚪</span>
          Logout
        </button>
      </div>
      </div>
      <div
        className={`sidebar__backdrop ${isOpen ? 'sidebar__backdrop--visible' : ''}`}
        onClick={onClose}
      />
    </>
  );
};

export default Sidebar;
